// src/components/practice/PartDisplay.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, ArrowLeft, CheckCircle, Clock, Volume2, Mic, Type, CheckSquare, Flag, Lightbulb, X } from 'lucide-react';
import { marked } from 'marked';
import WritingTask from './tasks/writing/WritingTask.jsx';
import StandardQuestionList from './tasks/StandardQuestionList.jsx';
import BlogPostComprehension from './tasks/BlogPostComprehension.jsx';
import SplitArticleComprehension from './tasks/SplitArticleComprehension.jsx';
import MatchingSituations from './tasks/MatchingSituations.jsx';
import OpinionMatching from './tasks/OpinionMatching.jsx';
import MatchingHeadlinesToOpinions from './tasks/MatchingHeadlinesToOpinions.jsx';
import MatchingHeadings from './tasks/MatchingHeadings.jsx';
import MatchingStatementsToSpeakers from './tasks/MatchingStatementsToSpeakers.jsx';
import TextReconstruction from './tasks/TextReconstruction.jsx';
import ClozeMultipleChoice from './tasks/ClozeMultipleChoice.jsx';
import ClozeClickToFill from './tasks/ClozeClickToFill.jsx';
import MatchingQuestionsToParagraphs from './tasks/MatchingQuestionsToParagraphs.jsx';
import MatchingHeadingsToTexts from './tasks/MatchingHeadingsToTexts.jsx';
import TextReconstructionEndings from './tasks/TextReconstructionEndings.jsx';
import ClozeFreeText from './tasks/ClozeFreeText.jsx';
import PresentationWithSummaryTask from './tasks/PresentationWithSummaryTask.jsx';
import SpeakingOrInfoTransferTask from './tasks/SpeakingOrInfoTransferTask.jsx';
import InformationTransferTable from './tasks/InformationTransferTable';
import ExamTimer from './ExamTimer.jsx';
import ReadingWordTools from './ReadingWordTools.jsx';

export const FlagButton = ({ questionId, flaggedQuestions, toggleFlagQuestion }) => {
    if (!questionId) return null;
    const isFlagged = Array.isArray(flaggedQuestions) && flaggedQuestions.includes(questionId);
    return (
        <button
            onClick={(e) => { e.stopPropagation(); toggleFlagQuestion(questionId); }}
            className="p-1 text-gray-400 hover:text-yellow-500 transition-colors flex-shrink-0"
            title={isFlagged ? "Markierung aufheben" : "Für die Überprüfung markieren"}
        >
            <Flag className={`w-5 h-5 ${isFlagged ? 'fill-yellow-400 text-yellow-500' : ''}`} />
        </button>
    );
};
export const renderContent = (partData) => {

  const { content, audio, taskType } = partData;
  if (!content && !audio) return null;
  const isTextEmbeddedTask = taskType.includes('reconstruction') || taskType.includes('cloze') || taskType.includes('blog-post-comprehension') || taskType.includes('matching-headings');
  return (
    <div className="mb-8 space-y-4">
        {content?.mainText && !isTextEmbeddedTask && (<div className="p-6 bg-gray-50 rounded-lg prose max-w-none whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: marked.parse(content.mainText) }}></div>)}
      {content?.texts && !isTextEmbeddedTask && !taskType.includes("matching") && !(taskType === 'article-comprehension' && content.texts.length > 1) && content.texts.map(text => (<div key={text.id || text.title} className="p-6 bg-gray-50 rounded-lg prose max-w-none"> {text.title && <h4 className="font-bold">{text.title}</h4>} <div className="text-gray-700 whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: text.content }} /> </div>))}
    </div>
  );
};

export  const renderExample = (question, partData) => {
    let answerText = question.correctAnswer;
    let exampleText = question.questionText || question.statement || question.situationText;
    let exampleContent = '';
     if (partData?.taskType === 'opinion-matching' && question.personId) {
        const opinionText = partData.content?.texts?.find(t => t.id === question.personId);
        if (opinionText) {
            exampleText = opinionText.author;
            exampleContent = `"${opinionText.content}"`;
        }
    }

    if (partData?.taskType === 'matching-headlines-opinions' && !exampleText) {
        const headline = partData.content?.headlines?.find(h => h.isExample || h.id === String(question.questionNumber));
        if (headline) exampleText = headline.text;
    }
    if (partData?.taskType === 'rules-comprehension' && partData?.questionType === 'matching' && exampleText) {
        const mainText = partData.content?.mainText || '';
        const paragraphs = mainText.split(/\n\s*\n/);
        const exampleParagraph = paragraphs.find(p => p.trim().startsWith(exampleText));
        if (exampleParagraph) { exampleContent = exampleParagraph.replace(exampleText, '').trim(); }
    }

    if (partData?.content?.options && typeof question.correctAnswer === 'string') {
        const matchingOption = partData.content.options.find(opt => opt.id === question.correctAnswer);
        if (matchingOption) { answerText = `${question.correctAnswer.toUpperCase()}) ${matchingOption.text}`; }
    } else if (question.options && typeof question.correctAnswer === 'string') {
        const matchingOption = question.options.find(opt => opt.id === question.correctAnswer);
        if (matchingOption) { answerText = `${question.correctAnswer.toUpperCase()}) ${matchingOption.text}`; }
    }
    if (typeof question.correctAnswer === 'boolean') { answerText = question.correctAnswer ? "Richtig" : "Falsch"; }
    if (partData?.questionType === 'true-false-not-in-text' && typeof question.correctAnswer === 'string') {
        const answerMap = {'+': 'Richtig', '-': 'Falsch', 'x': 'Nicht im Text'};
        answerText = answerMap[question.correctAnswer] || question.correctAnswer;
    }

    return (
        <div key={question.questionId} className="p-4 bg-gray-100 border-l-4 border-blue-400 rounded-r-lg mb-6">
            <p className="font-bold text-gray-700">Beispiel: {question.questionNumber || '0'}</p>
            <p className="my-2 font-semibold">{exampleText}</p>
            {exampleContent && <p className="my-2 text-gray-700">{exampleContent}</p>}
            <div className="flex items-center mt-2"> <CheckSquare className="w-5 h-5 text-green-600 mr-2" /> <span className="font-semibold text-green-700">Lösung: {answerText}</span> </div>
        </div>
    );
};
const TaskPreviewCard = ({ task, index, onSelect, provider }) => {
    const { scenario, inputContent, guidingPoints } = task;

    return (
        <div className="border-2 border-gray-200 rounded-lg p-6 bg-white transition-all hover:border-gray-800 hover:shadow-lg">
            <h3 className="font-bold text-xl text-gray-900 mb-4">
                {provider === 'OSD' ? `Variante ${task.id}` : `Thema ${index + 1}`}
            </h3>
            {scenario && <div className="prose prose-sm max-w-none mb-4" dangerouslySetInnerHTML={{ __html: marked.parse(scenario) }} />}
            {inputContent && <div className="border border-gray-300 p-3 rounded-md my-4 bg-gray-50/50 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: marked.parse(inputContent) }} />}
            {guidingPoints?.length > 0 && (
                <div className="mt-4">
                    <p className="font-semibold text-gray-700 mb-2">Punkte:</p>
                    <ul className="list-disc list-inside space-y-1 text-gray-600 prose-sm">
                        {guidingPoints.map((point, i) => <li key={i}>{point.text || point}</li>)}
                    </ul>
                </div>
            )}
            <button
                onClick={() => onSelect(index)}
                className="mt-6 w-full inline-flex items-center justify-center px-8 py-3 border-2 border-gray-900 text-base font-semibold rounded-md text-gray-900 bg-white cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-1 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900"
            >
                <CheckCircle className="w-5 h-5 mr-2" />
                Diese Aufgabe wählen
            </button>
        </div>
    );
};
const TaskSelection = ({ partData, onSelectTask }) => (
    <div className="max-w-4xl mx-auto my-8">
        <div className="text-center p-6 bg-white rounded-xl shadow-md border border-gray-200 mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Aufgabe wählen</h2>
            <p className="text-gray-600">
                {partData.instructions || "Bitte wählen Sie eine der folgenden Schreibaufgaben aus. Sie können Ihre Wahl später noch ändern."}
            </p>
        </div>
        <div className="space-y-6">
            {partData.tasks.map((task, index) => (
                <TaskPreviewCard
                    key={task.id || index}
                    task={task}
                    index={index}
                    onSelect={onSelectTask}
                    provider={partData.examProvider}
                />
            ))}
        </div>
    </div>
);


const PartDisplay = ({ partData, savedAnswers, onAnswerChange, flaggedQuestions, toggleFlagQuestion, goToNextPart, allParts, timeRemaining,
    currentSection,
    isWarning,
    isSpeakingSection = false,
    isCritical,
    isMobileAnswerSheetOpen,
    onCloseMobileAnswerSheet,
    onOpenMobileAnswerSheet,
    onHasAnswerSheetChange,
    onAnswerSheetOnlyNoTexts,
    enableReadingWordTools = false }) => {
    const { t } = useTranslation();
    const partContentRef = useRef(null);
    const [selectedTaskIndex, setSelectedTaskIndex] = useState(() => {
        return savedAnswers?.selectedTaskIndex ?? null;
    });
    const partAnswers = savedAnswers || {};
    const taskTypesWithAnswerSheet = [
        'text-reconstruction-endings', 'cloze-free-text', 'cloze-word-bank-click', 'matching-headings',
        'cloze-multiple-choice', 'blog-post-comprehension', 'article-comprehension', 'dual-article-comprehension', 'dual-article',
        'matching-situations', 'opinion-matching', 'rules-comprehension', 'matching-headlines-opinions',
        'matching-questions-to-paragraphs', 'information-transfer-notes', 'fill-blank', 'opinion-matching-forum',
        'podcast-comprehension', 'discussion-comprehension', 'opinion-matching-academic', 'matching-opinions',
        'text-reconstruction-sentence', 'text-reconstruction-paragraph', 'radio-interview', 'short-texts-announcements',
        'conversation', 'short-texts', 'mixed-comprehension-short-texts'
    ];
    const isWritingPart = [
        'email', 'email-formal', 'email-opinion', 'formal-letter', 'writing', 'essay', 'forum-post',
        'formal-complaint', 'formal-message', 'formal-response-email', 'choice-writing', 'choice-writing-essay'
    ].includes(partData?.taskType);
    useEffect(() => {
        if (typeof onHasAnswerSheetChange !== 'function') return;
        if (isSpeakingSection || isWritingPart) {
            onHasAnswerSheetChange(false);
        } else {
            onHasAnswerSheetChange(taskTypesWithAnswerSheet.includes(partData?.taskType));
        }
        return () => onHasAnswerSheetChange(false);
    }, [partData?.taskType, isSpeakingSection, isWritingPart, onHasAnswerSheetChange]);

    if (isSpeakingSection) {
  return (
    <div className="flex items-center justify-center min-h-[60vh] p-4">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl p-8 sm:p-12 border-2 border-gray-300 text-center">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-6">
          <Mic className="w-10 h-10 text-gray-400" />
        </div>
        <h2 className="text-3xl font-bold text-gray-800 mb-4">
          {t('speaking.comingSoon.title')}
        </h2>
        <p className="text-gray-600 text-lg mb-6">
          {t('speaking.comingSoon.description')}
        </p>
        <div className="inline-flex items-center px-6 py-3 bg-gray-100 text-gray-600 rounded-lg font-semibold">
          <Clock className="w-5 h-5 mr-2" />
          {t('speaking.comingSoon.badge')}
        </div>
      </div>
    </div>
  );
}
    const handleBackToSelection = () => {
        setSelectedTaskIndex(null);
    };

   if (isWritingPart) {
    const hasChoice = partData.optional && partData.tasks?.length > 1;

    if (hasChoice && selectedTaskIndex === null) {
        return <TaskSelection partData={partData} onSelectTask={setSelectedTaskIndex} />;
    }

    const taskData = hasChoice
        ? partData.tasks[selectedTaskIndex]
        : partData.task || (partData.tasks ? partData.tasks[0] : {});

    const finalPartData = { ...partData, task: taskData };

    // Add images for writing tasks
    let images = [];
    if (partData.image) {
        const provider = partData.examProvider?.toLowerCase() || 'goethe';
        const level = partData.level?.toLowerCase() || 'b2';
        const imagePath = `/data/exams/${provider}/${level}/${partData.image}`.replace(/ /g, '%20');
        console.log('PartDisplay - Writing task image path:', imagePath);
        images = [imagePath];
    }

    return (
        <WritingTask
            partData={finalPartData}
            savedAnswers={savedAnswers}
            onAnswerChange={onAnswerChange}
            onBackToSelection={hasChoice ? handleBackToSelection : null}
            selectedTaskIndex={hasChoice ? selectedTaskIndex : 0}
            goToNextPart={goToNextPart}
            images={images}
        />
    );
}

   const handleSingleAnswer = (questionId, answer) => {
        const newAnswers = { ...partAnswers, [questionId]: answer };
        onAnswerChange(partData.partId, newAnswers);
    };

    const handleBatchAnswerChange = (newAnswers) => {
        const updatedAnswers = { ...partAnswers, ...newAnswers };
        onAnswerChange(partData.partId, updatedAnswers);
    };

    const renderPartByType = () => {
        const props = {
            partData,
            partAnswers,
            handleSingleAnswer,
            handleBatchAnswerChange,
            flaggedQuestions,
            toggleFlagQuestion,
            isMobileAnswerSheetOpen,
            onCloseMobileAnswerSheet,
            onOpenMobileAnswerSheet,
            onHasAnswerSheetChange,
            onAnswerSheetOnlyNoTexts
        };
        const examples = partData.questions?.filter(q => q.isExample) || [];
        const regularQuestions = partData.questions?.filter(q => !q.isExample) || [];
        const allProps = { ...props, examples, questions: regularQuestions };

    const partContent = () => {

        switch (partData.taskType) {
            case 'text-reconstruction-endings': return <TextReconstructionEndings {...allProps} />;
            case 'cloze-free-text': return <ClozeFreeText {...allProps} />;
            case 'cloze-word-bank-click': return <ClozeClickToFill {...allProps} />;
            case 'matching-headings': return <MatchingHeadingsToTexts {...allProps} />;
            case 'cloze-multiple-choice': return <ClozeMultipleChoice {...allProps} />;
            case 'blog-post-comprehension': return <BlogPostComprehension {...allProps} />;
           case 'article-comprehension': {
  // Add images for Goethe B2 Reading Part 3
  let images = [];
  if (partData.image) {
    const provider = partData.examProvider?.toLowerCase() || 'goethe';
    const level = partData.level?.toLowerCase() || 'b2';
    images = [`/data/exams/${provider}/${level}/${partData.image}`];
  }
  return (partData.content?.texts && partData.content.texts.length > 1 && partData.examProvider !== 'TELC')
    ? <SplitArticleComprehension {...allProps} images={images} />
    : <StandardQuestionList {...allProps} images={images} />;
}
            case 'dual-article':
            case 'dual-article-comprehension': return <StandardQuestionList {...allProps} />;
            case 'matching-situations': return <MatchingSituations {...allProps} handleBatchAnswerChange={handleBatchAnswerChange} />;
            case 'opinion-matching': return <OpinionMatching {...allProps} />;
            case 'rules-comprehension':
                return partData.questionType === 'matching' ? <MatchingHeadings {...allProps} /> : <StandardQuestionList {...allProps} />;
            case 'matching-headlines-opinions': return <MatchingHeadlinesToOpinions {...allProps} />;
            case 'matching-questions-to-paragraphs': return <MatchingQuestionsToParagraphs {...allProps} />;

                case 'information-transfer-notes':
case 'fill-blank': return <ClozeFreeText {...allProps} />;
            case 'opinion-matching-forum':
                case 'podcast-comprehension':
            case 'discussion-comprehension':
                if (partData.questionType === 'multiple-choice') return <StandardQuestionList {...allProps} />;
                return <MatchingStatementsToSpeakers {...allProps} />;
            case 'opinion-matching-academic':
            case 'matching-opinions': return <MatchingStatementsToSpeakers {...allProps} />;
            case 'text-reconstruction-sentence':
case 'text-reconstruction-paragraph': {
  // Add image for Goethe B2 Reading Part 2
  let images = [];
  if (partData.image) {
    const provider = partData.examProvider?.toLowerCase() || 'goethe';
    const level = partData.level?.toLowerCase() || 'b2';
    images = [`/data/exams/${provider}/${level}/${partData.image}`];
  }
  return <TextReconstruction {...allProps} images={images} />;
}
            case 'radio-interview':
            case 'short-texts-announcements':
            case 'conversation':
            case 'short-texts':
            case 'mixed-comprehension-short-texts': return <StandardQuestionList {...allProps} />;
            case 'choice-writing-essay':
            case 'choice-writing':
            case 'email':
            case 'email-opinion':
            case 'email-formal':
            case 'email-response-community':
            case 'email-response-professional':
            case 'forum-post':
            case 'formal-response-email':
            case 'formal-complaint':
            case 'formal-message':
            case 'formal-letter': {
    // Add images for writing tasks (especially forum-post)
    let images = [];
    if (partData.image) {
        const provider = partData.examProvider?.toLowerCase() || 'goethe';
        const level = partData.level?.toLowerCase() || 'b2';
        // Build path and replace spaces with %20
        const imagePath = `/data/exams/${provider}/${level}/${partData.image}`.replace(/ /g, '%20');
        images = [imagePath];
    }
    return <WritingTask {...props} images={images} />;
}
            case 'presentation-with-summary': return <PresentationWithSummaryTask {...props} />;
            case 'conversation-introductory':
            case 'planning':
            case 'presentation':
            case 'discussion-feedback':

           case 'information-transfer': {
  // Special case: ÖSD B2 Listening Part 2 - Table format
  if (partData?.examProvider?.toUpperCase() === 'OSD' &&
      partData?.level?.toUpperCase() === 'B2' &&
      partData?.partNumber === 2 &&
      partData?.questionType === 'table-completion') {

      // Scored gaps live in partData.questions; answers save like any other part.
      return (
        <InformationTransferTable
          tableData={partData.table}
          questions={partData.questions || []}
          answers={partAnswers}
          onAnswer={handleSingleAnswer}
        />
      );
  }
    // Default speaking/info transfer tasks
    return <SpeakingOrInfoTransferTask {...props} />;
}
            default: if (regularQuestions.length > 0) { return <StandardQuestionList {...allProps} />; }
                return (<div className="p-4 bg-yellow-100 text-yellow-800 rounded-lg">Aufgabentyp "<strong>{partData.taskType}</strong>" wird noch nicht vollständig unterstützt.</div>);
        }
    };

  return (
      <div className="w-full min-h-screen bg-gray-50">
           {partData.instructions && !isWritingPart && (
    <div className="mb-4 sm:mb-6 border-2 border-gray-200 bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="p-3 sm:p-4 bg-white">
            {/* Compact single-row header: title + hint + timer */}
            <div className="flex items-center justify-between gap-3 mb-3 sm:mb-4">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                    <FileText className="w-5 h-5 text-gray-700 shrink-0" />
                    <h3 className="text-sm sm:text-base font-bold uppercase tracking-wide text-gray-900 truncate">
                        Anweisungen
                    </h3>
                    <span className="hidden sm:inline text-xs text-gray-500 font-medium truncate">
                        Bitte sorgfältig lesen
                    </span>
                </div>
                {timeRemaining !== undefined && timeRemaining > 0 && (
                    <div className="shrink-0">
                        <ExamTimer
                            timeRemaining={timeRemaining}
                            sectionName={currentSection?.sectionName || ''}
                            isWarning={isWarning}
                            isCritical={isCritical}
                            totalTime={currentSection?.duration ? currentSection.duration * 60 : 0}
                            inline={true}
                        />
                    </div>
                )}
            </div>
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-2 sm:hidden">
                Bitte lesen Sie die folgenden Anweisungen sorgfältig durch
            </p>
            <div
                className="prose prose-sm sm:prose-base max-w-none text-gray-900 leading-relaxed prose-p:my-1.5 prose-ul:my-2 prose-li:my-0"
                style={{ fontSize: '0.9375rem', lineHeight: '1.6' }}
                dangerouslySetInnerHTML={{ __html: marked.parse(partData.instructions) }}
            />
        </div>
        <div className="h-1 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200" aria-hidden />
    </div>
)}

                <div className="w-full" ref={partContentRef}>
                    {partContent()}
                </div>

                <ReadingWordTools
                  enabled={enableReadingWordTools}
                  containerRef={partContentRef}
                  partData={partData}
                />
            </div>
        );
    };

    return renderPartByType();
};

export default PartDisplay;